/**
 * Reports Hub (UI-11)
 *
 * Grouped tiles with icons — uses Thaklis brand colors.
 * Route: /reports. Only reports on real data open; the rest show "Coming soon" until their backend step lands.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import {
  Box, Typography, Paper, TextField, InputAdornment, IconButton, useTheme,
} from '@mui/material';
import {
  Search as SearchIcon, Favorite, FavoriteBorder,
  TrendingUp, AccountBalance, MoneyOff, AccountBalanceWallet, Today,
  ReceiptLong, HourglassBottom, People,
  MonetizationOn, Handshake, PersonSearch,
  Percent, FileDownload, FileUpload,
  Savings, CompareArrows, MenuBook, Inventory, Receipt,
  Description, ShowChart, DonutLarge, Groups, Payments, LocalShipping, EventNote, PersonOff, CalendarMonth,
} from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { getBrandInk, getBrandTint } from '../../utils/colorUtils';
import { reportFavouritesService } from '../../services/accounting/reportFavourites.service';

const heartPop = keyframes`
  0% { transform: scale(0.8); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
`;

const REPORT_GROUPS = [
  {
    title: 'Financial',
    reports: [
      { name: 'P&L Statement', desc: 'Profit by revenue and expense', icon: TrendingUp, path: '/reports/profit-loss' },
      { name: 'Balance Sheet', desc: 'Assets, liabilities and equity', icon: AccountBalance, path: '/reports/balance-sheet' },
      { name: 'Trial Balance', desc: 'Every account, debits and credits', icon: Description, path: '/reports/trial-balance' },
      { name: 'General Ledger', desc: 'One account, every line, running balance', icon: ShowChart, path: '/reports/general-ledger' },
      { name: 'Cash Flow', desc: 'Operating, investing, financing', icon: MoneyOff, path: '/reports/cash-flow' },
      { name: 'Cash Position', desc: 'Live balance all accounts', icon: AccountBalanceWallet, path: '/reports/cash-position' },
      { name: 'Daily Collection', desc: 'Money collected by method', icon: Today, path: '/reports/collection' },
      { name: 'Bank Statement', desc: 'Account ledger, running balance', icon: AccountBalance, path: '/reports/wallet-bank' },
      { name: 'Daily Sheet', desc: 'Everything posted on a day', icon: EventNote, path: '/reports/daily-sheet' },
      { name: 'Annual P&L', desc: 'Profit and loss month by month', icon: CalendarMonth, path: '/reports/annual-pl' },
    ],
  },
  {
    title: 'Customer',
    reports: [
      { name: 'Client Statements', desc: 'Invoices, payments and aging', icon: ReceiptLong, path: '/reports/client-ledger' },
      { name: 'Receivables Aging', desc: 'Outstanding 30/60/90+ days', icon: HourglassBottom, path: '/reports/receivables-aging' },
      { name: 'Customer Advances', desc: 'Wallet vs outstanding', icon: People, path: '/reports/customer-advances-debtors' },
      { name: 'Inactive Clients', desc: 'No activity for 30 to 180 days', icon: PersonOff, path: '/reports/inactive-clients' },
    ],
  },
  {
    title: 'Sales',
    reports: [
      { name: 'Invoice Report', desc: 'Revenue, cost and profit per invoice', icon: Description, path: '/reports/invoice-report' },
      { name: 'Profit Report', desc: 'Profit and margin by service', icon: ShowChart, path: '/reports/service-profit?sort=PROFIT' },
      { name: 'Service Mix', desc: 'Services by volume', icon: DonutLarge, path: '/reports/service-profit?sort=VOLUME' },
      { name: 'Agent Performance', desc: 'Revenue and commission', icon: PersonSearch, path: '/reports/agent-performance' },
    ],
  },
  {
    title: 'Expense & Cost',
    reports: [
      { name: 'Expense Report', desc: 'By category, vendor, method', icon: MonetizationOn, path: '/reports/expenses' },
      { name: 'Supplier Report', desc: 'Providers and expense vendors', icon: LocalShipping, path: '/reports/suppliers' },
      { name: 'Commission Payable', desc: 'Owed to agents and staff', icon: Handshake, path: '/accounting/commissions' },
      { name: 'Staff Commission', desc: 'Earned, paid and owed per person', icon: Groups, path: '/reports/staff-commission' },
      { name: 'Salary Report', desc: 'Salaries paid per person and month', icon: Payments, path: '/reports/salary' },
    ],
  },
  {
    title: 'Tax',
    reports: [
      { name: 'VAT Return', desc: 'Output - Input = Payable', icon: Percent, path: '/reports/vat-return' },
      { name: 'Tax Input Report', desc: 'Recoverable VAT purchases', icon: FileDownload, path: '/reports/vat-return?tab=input' },
      { name: 'Tax Output Report', desc: 'VAT collected on sales', icon: FileUpload, path: '/reports/vat-return?tab=output' },
    ],
  },
  {
    title: 'Management',
    reports: [
      { name: 'Owner Drawings', desc: 'Withdrawals and profit share', icon: Savings, path: '/accounting/drawings' },
      { name: 'Reconciliation', desc: 'Match bank and cash', icon: CompareArrows, path: '/accounting/reconciliation' },
      { name: 'Journal Entries', desc: 'GL entries and adjustments', icon: MenuBook, path: '/accounting/journal-entries' },
      { name: 'Fixed Assets', desc: 'Register and depreciation', icon: Inventory, path: '/accounting/assets' },
      { name: 'Invoice Summary', desc: 'Invoices vs receipts', icon: Receipt, path: '/reports/invoice-receipt-summary' },
    ],
  },
];

// Reports wired to the real backend (FRONTEND-INTEGRATION.md §6b); add a path when its wave is done.
const LIVE_PATHS = new Set(['/accounting/expenses', '/accounting/commissions', '/reports/vat-return', '/reports/profit-loss', '/reports/client-ledger', '/reports/balance-sheet', '/reports/trial-balance', '/reports/general-ledger', '/accounting/journal-entries', '/accounting/drawings', '/accounting/assets', '/accounting/reconciliation', '/reports/collection', '/reports/wallet-bank', '/reports/customer-advances-debtors', '/reports/invoice-receipt-summary', '/reports/cash-flow', '/reports/cash-position', '/reports/receivables-aging', '/reports/vat-return?tab=input', '/reports/vat-return?tab=output', '/reports/invoice-report', '/reports/service-profit?sort=PROFIT', '/reports/service-profit?sort=VOLUME', '/reports/agent-performance', '/reports/staff-commission', '/reports/salary', '/reports/expenses', '/reports/suppliers', '/reports/daily-sheet', '/reports/annual-pl', '/reports/inactive-clients']);

const ReportTile = ({ report, live, favourite, onToggleFavourite, onOpen, brandInk, brandTint, brandTintHover }) => {
  const Icon = report.icon;
  const [popped, setPopped] = useState(false);
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        cursor: live ? 'pointer' : 'default',
        opacity: live ? 1 : 0.55,
        transition: 'all 0.15s',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        '&:hover': live ? {
          borderColor: brandInk,
          bgcolor: brandTintHover,
        } : {},
      }}
      onClick={() => live && onOpen(report.path)}
    >
      <Box sx={{
        width: 38,
        height: 38,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: brandTint,
        flexShrink: 0,
      }}>
        <Icon sx={{ fontSize: 19, color: live ? brandInk : 'text.disabled' }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, color: live ? 'text.primary' : 'text.disabled', lineHeight: 1.3 }}>
            {report.name}
          </Typography>
          {!live && (
            <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: fw.semibold, textTransform: 'uppercase', letterSpacing: '0.04em',
              px: 0.75, py: 0.1, borderRadius: 1, border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>
              Coming soon
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>
          {report.desc}
        </Typography>
      </Box>
      {live && (
        <IconButton size="small" aria-label={favourite ? 'Remove from favourites' : 'Add to favourites'}
          onClick={(e) => { e.stopPropagation(); setPopped(true); onToggleFavourite(report.path); }}>
          {favourite
            ? <Favorite key="on" sx={{ fontSize: 18, color: brandInk, animation: popped ? `${heartPop} 0.3s ease-out` : 'none' }} />
            : <FavoriteBorder key="off" sx={{ fontSize: 18, color: 'text.secondary', animation: popped ? `${heartPop} 0.3s ease-out` : 'none' }} />}
        </IconButton>
      )}
    </Paper>
  );
};

const ReportsHub = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [favourites, setFavourites] = useState([]);

  useEffect(() => {
    reportFavouritesService.get().then(setFavourites).catch(() => setFavourites([]));
  }, []);

  const toggleFavourite = (path) => {
    const previous = favourites;
    const next = previous.includes(path) ? previous.filter((p) => p !== path) : [...previous, path];
    setFavourites(next);
    reportFavouritesService.save(next).catch(() => setFavourites(previous));
  };

  const brandInk = getBrandInk(theme);
  const brandTint = getBrandTint(theme, 0.08);
  const brandTintHover = getBrandTint(theme, 0.15);

  const matches = (r) => r.name.toLowerCase().includes(search.toLowerCase()) || r.desc.toLowerCase().includes(search.toLowerCase());

  const filteredGroups = REPORT_GROUPS.map((group) => ({
    ...group,
    reports: group.reports.filter(matches),
  })).filter((g) => g.reports.length > 0);

  const favouriteReports = useMemo(
    () => favourites
      .map((path) => REPORT_GROUPS.flatMap((g) => g.reports).find((r) => r.path === path && LIVE_PATHS.has(r.path)))
      .filter(Boolean)
      .filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favourites, search],
  );

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Reports</Typography>
        <TextField
          size="small"
          placeholder="Search reports..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>
            ),
          }}
        />
      </Box>

      {favouriteReports.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: fw.bold, textTransform: 'uppercase', color: brandInk, letterSpacing: '0.05em' }}>
              Favourites
            </Typography>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: brandTint }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {favouriteReports.map((report) => (
              <ReportTile key={report.path} report={report} live favourite onToggleFavourite={toggleFavourite} onOpen={navigate}
                brandInk={brandInk} brandTint={brandTint} brandTintHover={brandTintHover} />
            ))}
          </Box>
        </Box>
      )}

      {filteredGroups.map((group) => (
        <Box key={group.title} sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: fw.bold, textTransform: 'uppercase', color: brandInk, letterSpacing: '0.05em' }}>
              {group.title}
            </Typography>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: brandTint }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {group.reports.map((report) => {
              const live = Boolean(report.path && LIVE_PATHS.has(report.path));
              return (
                <ReportTile key={report.name} report={report} live={live} favourite={favourites.includes(report.path)}
                  onToggleFavourite={toggleFavourite} onOpen={navigate}
                  brandInk={brandInk} brandTint={brandTint} brandTintHover={brandTintHover} />
              );
            })}
          </Box>
        </Box>
      ))}

      {filteredGroups.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography sx={{ fontSize: fs.sm }}>No reports match &ldquo;{search}&rdquo;</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ReportsHub;
